// Story BP-8: tabs top-level (Needs Attention default + All Users) +
// sub-tab Telemetry dentro do mesmo panel (PC.6).

import React, { useState, useMemo } from 'react';
import { Loader2, AlertCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../ui/Input';
import {
  useNeedsAttention,
  useAllBillingUsers,
  type AdminBillingUser,
} from '../../hooks/useAdminBilling';
import { AdminBillingUserRow } from './AdminBillingUserRow';
import { AdminBillingTelemetry } from './AdminBillingTelemetry';

export type AdminBillingTab = 'needs_attention' | 'all_users' | 'telemetry';

export interface AdminBillingTabsProps {
  onOpenOverride: (user: AdminBillingUser) => void;
  refreshKey?: number;
}

export const AdminBillingTabs: React.FC<AdminBillingTabsProps> = ({
  onOpenOverride,
  refreshKey: _refreshKey,
}) => {
  const [tab, setTab] = useState<AdminBillingTab>('needs_attention');

  return (
    <div>
      <div className="flex items-center gap-1 mb-6 border-b border-white/10 overflow-x-auto">
        <TabButton
          active={tab === 'needs_attention'}
          onClick={() => setTab('needs_attention')}
        >
          Needs Attention
        </TabButton>
        <TabButton active={tab === 'all_users'} onClick={() => setTab('all_users')}>
          All Users
        </TabButton>
        <TabButton active={tab === 'telemetry'} onClick={() => setTab('telemetry')}>
          Telemetria
        </TabButton>
      </div>

      {tab === 'needs_attention' && <NeedsAttentionView onOpenOverride={onOpenOverride} />}
      {tab === 'all_users' && <AllUsersView onOpenOverride={onOpenOverride} />}
      {tab === 'telemetry' && <AdminBillingTelemetry />}
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
      active
        ? 'text-white border-primary-500'
        : 'text-neutral-400 hover:text-white border-transparent'
    }`}
  >
    {children}
  </button>
);

// ─── Needs Attention ─────────────────────────────────────────────

const NeedsAttentionView: React.FC<{
  onOpenOverride: (user: AdminBillingUser) => void;
}> = ({ onOpenOverride }) => {
  const { users, loading, error } = useNeedsAttention();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-neutral-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-red-300">Erro ao carregar</p>
          <p className="text-xs">{error}</p>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-16 rounded-lg border border-white/5 bg-neutral-900/30">
        <p className="text-lg text-white mb-2">🎉</p>
        <p className="text-sm text-neutral-300 font-medium">Nenhum user precisa de atenção no momento</p>
        <p className="text-xs text-neutral-500 mt-1">
          Grandfather expirando D≤30 e pagamentos past_due aparecem aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {users.map(u => (
        <AdminBillingUserRow
          key={u.id}
          user={u}
          onOpenOverride={onOpenOverride}
          showReason
        />
      ))}
    </div>
  );
};

// ─── All Users ───────────────────────────────────────────────────

const PAGE_SIZE = 50;

const AllUsersView: React.FC<{
  onOpenOverride: (user: AdminBillingUser) => void;
}> = ({ onOpenOverride }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { users, total, loading, error } = useAllBillingUsers({
    search: debouncedSearch || null,
    page,
    perPage: PAGE_SIZE,
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por email ou ID..."
          className="w-full bg-neutral-900 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-base sm:text-sm text-white placeholder:text-neutral-500"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-neutral-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {error}
        </div>
      ) : users.length === 0 ? (
        <p className="text-sm text-neutral-500 italic py-8 text-center">
          {debouncedSearch ? 'Nenhum usuário encontrado.' : 'Nenhum usuário cadastrado ainda.'}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            {users.map(u => (
              <AdminBillingUserRow key={u.id} user={u} onOpenOverride={onOpenOverride} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 pt-2">
              <p className="text-xs text-neutral-500">
                Página {page} de {totalPages} · {total} usuários
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Próxima"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Input primitive import kept for TS happiness — not used in this file but
// re-exported for convenience elsewhere if needed.
void Input;
